import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import Swal from 'sweetalert2';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import loadingGif from '../assets/loading.gif';
import { IconButton } from '@mui/material';
import {
  Box,
  Card,
  Typography,
  Button,
  Divider,
  LinearProgress,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
} from '@mui/material';
import './Profile.css';

const Profile = () => {
  const { memberId } = useParams();
  const [memberEmail, setMemberEmail] = useState('');
  const [memberFirstName, setMemberFirstName] = useState('');
  const [memberLastName, setMemberLastName] = useState('');
  const [memberNumber, setMemberNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');


  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const isPasswordValid = password && confirmPassword && password === confirmPassword;

  const navigate = useNavigate();

  useEffect(() => {
    const fetchMemberData = async () => {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('selectedOrg');
      const userEmail = localStorage.getItem('userEmail');

      if (!token || !orgId) {
        navigate('/unauthorized');
        return;
      }

      setLoading(true);

      try {
        const response = await axios.get(`${API_BASE_URL}/org-members/member-profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
            MemberId: userEmail,
          },
        });

        const data = response.data.memberDetails;

        setMemberEmail(data.memberEmail || '');
        setMemberFirstName(data.memberFirstName || '');
        setMemberLastName(data.memberLastName || '');
        setMemberNumber(data.memberPhoneNumber || '');

      } catch (error) {
        console.error('Error fetching member data:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to retrieve member information.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [navigate, memberId]);

  const handleEditMember = async () => {
    if (!memberEmail || !memberFirstName || !memberLastName || !memberNumber) {
        Swal.fire({
          title: 'Error!',
          text: 'Please fill out all fields.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } else {
        try {
          const token = localStorage.getItem('token');
          const orgId = localStorage.getItem('selectedOrg');
    
          const response = await fetch(`${API_BASE_URL}/org-members/edit`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              SelectedOrg: orgId,
            },
            body: JSON.stringify({
              memberEmail: memberEmail,
              memberPassword: isPasswordValid ? password : -1,
              memberFirstName,
              memberLastName,
              memberNumber,
            }),
          });
    
          if (response.status === 201) {
            const data = await response.json();
    
            Swal.fire({
              title: 'Success!',
              text: 'Member updated successfully!',
              icon: 'success',
              confirmButtonText: 'OK',
            });
          } else if (response.status === 409) {
            Swal.fire({
              title: 'Conflict',
              text: 'The member already exists in the organization.',
              icon: 'warning',
              confirmButtonText: 'OK',
            });
          } else {
            throw new Error('Failed to edit member');
          }
    
        } catch (error) {
          console.error('Error creating member:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to edit member.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
    };
  }

  const handleEditMemberInfo = async () => {
    if (!memberEmail || !memberFirstName || !memberLastName || !memberNumber) {
        Swal.fire({
          title: 'Error!',
          text: 'Please fill out all fields.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } else {
        try {
          const token = localStorage.getItem('token');
          const orgId = localStorage.getItem('selectedOrg');
    
          const response = await fetch(`${API_BASE_URL}/org-members/edit`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              SelectedOrg: orgId,
            },
            body: JSON.stringify({
              memberEmail: memberEmail,
              memberPassword: -1,
              memberFirstName,
              memberLastName,
              memberNumber,
            }),
          });

          if (response.status === 201) {
            const data = await response.json();
    
            Swal.fire({
              title: 'Success!',
              text: 'Member updated successfully!',
              icon: 'success',
              confirmButtonText: 'OK',
            });
          } else if (response.status === 409) {
            Swal.fire({
              title: 'Conflict',
              text: 'The member already exists in the organization.',
              icon: 'warning',
              confirmButtonText: 'OK',
            });
          } else {
            throw new Error('Failed to edit member');
          }
    
        } catch (error) {
          console.error('Error creating member:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to edit member.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
    };
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    const formData = new FormData();
    formData.append('photo', selectedFile);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/profile/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
          userEmail: memberEmail,
        }
      });
      
      Swal.fire({
        title: 'Success!',
        text: 'Profile picture updated successfully!',
        icon: 'success',
        confirmButtonText: 'OK'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.reload();
        }
      });
    } catch (error) {
      console.error('Error uploading photo:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to upload profile picture',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  if (loading) {
    return (
      <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          backgroundColor: '#fff',
      }}>
          <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
      </div>
      );
    }

  return (
    <Box sx={{ display: 'flex', gap: 3, p: 3 }}>
      {/* Left Section - Personal Info */}
      <Card sx={{ flex: 1, p: 3 }}>
        <Typography variant="h6" gutterBottom>Personal Information</Typography>
        <TextField
          fullWidth
          label="Email"
          value={memberEmail}
          disabled
          margin="normal"
          multiline
          rows={1}
        />
        <TextField
          fullWidth
          label="First Name"
          value={memberFirstName}
          onChange={(e) => setMemberFirstName(e.target.value)}
          margin="normal"
          multiline
          rows={1}
        />
        <TextField
          fullWidth
          label="Last Name"
          value={memberLastName}
          onChange={(e) => setMemberLastName(e.target.value)}
          margin="normal"
          multiline
          rows={1}
        />
        <TextField
          fullWidth
          label="Phone Number"
          value={memberNumber}
          onChange={(e) => setMemberNumber(e.target.value)}
          margin="normal"
          multiline
          rows={1}
        />
        <Button
          variant="contained"
          onClick={handleEditMemberInfo}
          sx={{ mt: 2 }}
        >
          Update Personal Info
        </Button>
      </Card>

      {/* Right Section - Password Change */}
      <Card sx={{ flex: 1, p: 3 }}>
        <Typography variant="h6" gutterBottom>Change Password</Typography>
        <TextField
          fullWidth
          label="New Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          margin="normal"
        />
        <Button
          variant="contained"
          onClick={handleEditMember}
          disabled={!isPasswordValid}
          sx={{ mt: 2 }}
        >
          Update Password
        </Button>
      </Card>
      <Card sx={{ flex: 1, p: 3 }}>
        <Typography variant="h6" gutterBottom>Profile Picture</Typography>
          {previewUrl ? (
            <img 
              src={previewUrl} 
              alt="Preview" 
              style={{ 
                width: 150, 
                height: 150, 
                borderRadius: '50%', 
                objectFit: 'cover', 
                marginBottom: 16 
              }} 
            />
          ) : (
            <div style={{
              width: 150,
              height: 150,
              borderRadius: '50%',
              backgroundColor: '#f0f0f0',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="body2" color="textSecondary">
                No Image Preview
              </Typography>
            </div>
          )}
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="profile-picture-upload"
        />
        <label htmlFor="profile-picture-upload">
          <Button variant="contained" component="span" sx={{ mr: 2 }}>
            Choose Image
          </Button>
        </label>
        <Button 
          variant="contained" 
          onClick={handleUpload}
          disabled={!selectedFile}
        >
          Upload
        </Button>
      </Card>
    </Box>
  );
};

export default Profile;
